import React, { useCallback, useMemo, useState, useRef } from 'react';
import * as d3 from 'd3';
import useResizeObserver from 'use-resize-observer';
import { makeStyles } from '~/lib/bn-material-ui';
import InventoryMapZoomControl from './inventory-map-zoom-control';
import useD3 from '~/common/use-d3';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    border: `1px solid ${theme.palette.gray[30]}`,
    borderBottom: 'none',
    borderRadius: 0,
    overflow: 'hidden',
  },
  svg: {
    background: theme.palette.white,
    width: '100%',
    height: '100%',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  bin: {
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(0.85) !important',
    },
  },
  binStylesDefault: {
    fill: theme.palette.gray[20],
    stroke: theme.palette.gray[80],
    strokeWidth: 1,
  },
  selectedBin: {
    filter: 'brightness(0.85) !important',
  },
}));

// Types for the bin data structure
export const BinShape = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
};

export const BinTypes = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
};

/**
 * Background shape types
 */
export const BackgroundShapeTypes = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  POLYGON: 'polygon',
  LINE: 'line',
  TEXT: 'text',
  IMAGE: 'image',
};

const MIN_ZOOM = 2000;
const INITIAL_ZOOM = 9500;
const MAX_ZOOM = 25000;

const CENTER_OFFSET_LAT = -0.00006;
const CENTER_OFFSET_LON = 0;

/**
 * Background shape data structure
 * @typedef {Object} BackgroundShape
 * @property {string} id - Unique identifier for the shape
 * @property {string} type - Shape type ('rectangle', 'circle', 'polygon', 'line', 'text')
 * @property {number} latitude - Latitude coordinate (for rectangle, circle, polygon center, line start, or text position)
 * @property {number} longitude - Longitude coordinate (for rectangle, circle, polygon center, line start, or text position)
 * @property {Object} dimensions - Shape dimensions
 * @property {number} dimensions.width - Width for rectangular shapes
 * @property {number} dimensions.height - Height for rectangular shapes
 * @property {number} dimensions.radius - Radius for circular shapes
 * @property {Array} dimensions.points - Array of {latitude, longitude} points for polygon and line shapes
 * @property {string} dimensions.text - Text content for text shapes
 * @property {number} dimensions.fontSize - Font size in meters for text shapes
 * @property {number} dimensions.angle - Rotation angle in degrees for text shapes (0 = horizontal, 90 = vertical)
 * @property {string} href - Image URL for image shapes
 * @property {Object} style - Styling information
 * @property {string} style.fill - Fill color
 * @property {string} style.stroke - Stroke color
 * @property {number} style.strokeWidth - Stroke width
 * @property {number} style.opacity - Opacity value
 */

/**
 * Bin data structure
 * @typedef {Object} Bin
 * @property {string} id - Unique identifier for the bin
 * @property {string} name - Display name for the bin
 * @property {number} latitude - Latitude coordinate
 * @property {number} longitude - Longitude coordinate
 * @property {string} shape - Shape type ('rectangle' or 'circle')
 * @property {Object} dimensions - Bin dimensions
 * @property {number} dimensions.width - Width for rectangular bins
 * @property {number} dimensions.height - Height for rectangular bins
 * @property {number} dimensions.radius - Radius for circular bins
 */

/**
 * Bin style configuration
 * @typedef {Object} BinStyle
 * @property {string} fill - Fill color
 * @property {string} stroke - Stroke color
 * @property {number} strokeWidth - Stroke width
 * @property {number} opacity - Opacity value
 */

/**
 * Hook to calculate map bounds from bin data
 * @param {Bin[]} bins - Array of bin data
 * @returns {Object} Map bounds with padding
 */
function useMapBounds(bins, backgroundShapes = []) {
  return useMemo(() => {
    const allLats = [];
    const allLngs = [];

    // Add bin coordinates
    if (bins && bins.length > 0) {
      allLats.push(...bins.map(bin => bin.latitude));
      allLngs.push(...bins.map(bin => bin.longitude));
    }

    // Add background shape coordinates
    if (backgroundShapes && backgroundShapes.length > 0) {
      backgroundShapes.forEach(shape => {
        allLats.push(shape.latitude);
        allLngs.push(shape.longitude);

        // For polygon and line shapes, add all points
        if (shape.dimensions.points) {
          shape.dimensions.points.forEach(point => {
            allLats.push(point.latitude);
            allLngs.push(point.longitude);
          });
        }
      });
    }

    if (allLats.length === 0) {
      return {
        minLat: 0,
        maxLat: 1,
        minLng: 0,
        maxLng: 1,
      };
    }

    const padding = 0.01; // 1% padding
    const latRange = Math.max(...allLats) - Math.min(...allLats);
    const lngRange = Math.max(...allLngs) - Math.min(...allLngs);

    return {
      minLat: Math.min(...allLats) - latRange * padding,
      maxLat: Math.max(...allLats) + latRange * padding,
      minLng: Math.min(...allLngs) - lngRange * padding,
      maxLng: Math.max(...allLngs) + lngRange * padding,
    };
  }, [bins, backgroundShapes]);
}

/**
 * Hook to create D3 scales for coordinate mapping
 * @param {Object} bounds - Map bounds
 * @param {number} width - Container width
 * @param {number} height - Container height
 * @returns {Object} D3 scales for x and y coordinates
 */
function useMapScales(bounds, width, height) {
  return useMemo(() => {
    const xScale = d3.scaleLinear()
      .domain([bounds.minLng, bounds.maxLng])
      .range([50, width - 50]); // 50px padding on sides

    const yScale = d3.scaleLinear()
      .domain([bounds.minLat, bounds.maxLat])
      .range([height - 50, 50]); // 50px padding on top/bottom, flip Y axis

    return { xScale, yScale };
  }, [bounds, width, height]);
}

/**
 * Hook to create a scale for converting meters to pixels
 * @param {Object} bounds - Map bounds
 * @param {number} width - Container width
 * @param {number} height - Container height
 * @returns {Function} Scale function that converts meters to pixels
 */
function useMetersToPixelsScale(bounds, width, height) {
  return useMemo(() => {
    // Calculate the approximate meters per degree at the center of the map
    // This is a rough approximation - for more accuracy, you'd need to use a proper projection
    const centerLat = (bounds.minLat + bounds.maxLat) / 2;

    // Approximate meters per degree (varies by latitude)
    const metersPerDegreeLat = 111320; // meters per degree latitude (constant)
    const metersPerDegreeLng = 111320 * Math.cos(centerLat * Math.PI / 180); // meters per degree longitude

    // Calculate the scale factors
    const latRange = bounds.maxLat - bounds.minLat;
    const lngRange = bounds.maxLng - bounds.minLng;

    const metersPerPixelLat = (latRange * metersPerDegreeLat) / (height - 100); // 100px total padding
    const metersPerPixelLng = (lngRange * metersPerDegreeLng) / (width - 100); // 100px total padding

    // Use the smaller scale to ensure bins don't get too large
    const metersPerPixel = Math.min(metersPerPixelLat, metersPerPixelLng);

    return (meters) => meters / metersPerPixel;
  }, [bounds, width, height]);
}

const calculateZoomPercentage = (currentZoom) => {
  // Center the zoom percentage around INITIAL_ZOOM = 100%
  const logMin = Math.log(MIN_ZOOM);
  const logMax = Math.log(MAX_ZOOM);
  const logInitial = Math.log(INITIAL_ZOOM);
  const logCurrent = Math.log(currentZoom);

  // Calculate the range from MIN_ZOOM to MAX_ZOOM
  const logRange = logMax - logMin;

  // Calculate position relative to MIN_ZOOM
  const logPosition = logCurrent - logMin;

  // Calculate where INITIAL_ZOOM should be (100%)
  const initialPosition = logInitial - logMin;
  const initialPercentage = (initialPosition / logRange) * 400;

  // Adjust the calculation so INITIAL_ZOOM = 100%
  const adjustedPercentage = (logPosition / logRange) * 400;
  const offset = 100 - initialPercentage;

  return Math.max(0, Math.min(400, adjustedPercentage + offset));
};

/**
 * Hook to manage zoom and pan behavior
 * @param {number} width - Container width
 * @param {number} height - Container height
 * @param {Function} onViewChange - Callback when view changes (zoom or pan)
 * @param {Bin[]} bins - Array of bin data
 * @param {Object} bounds - Map bounds
 * @param {Function} setIsInteracting - Function to set interaction state
 * @returns {Object} Zoom behavior and transform
 */
function useZoomBehavior(width, height, onViewChange, bins, bounds, setIsInteracting) {
  // Calculate center of all bins
  const binCenter = useMemo(() => {
    if (!bins || bins.length === 0) {
      return { lat: 0, lng: 0 };
    }

    const totalLat = bins.reduce((sum, bin) => sum + bin.latitude, 0);
    const totalLng = bins.reduce((sum, bin) => sum + bin.longitude, 0);

    return {
      lat: totalLat / bins.length + CENTER_OFFSET_LAT,
      lng: totalLng / bins.length + CENTER_OFFSET_LON,
    };
  }, [bins]);

  // Calculate initial transform to center on bin center
  const initialTransform = useMemo(() => {
    if (!bins || bins.length === 0) {
      return d3.zoomIdentity;
    }

    // Create temporary scales to calculate center position
    const tempXScale = d3.scaleLinear()
      .domain([bounds.minLng, bounds.maxLng])
      .range([50, width - 50]);

    const tempYScale = d3.scaleLinear()
      .domain([bounds.minLat, bounds.maxLat])
      .range([height - 50, 50]);

    // Calculate center position in pixel coordinates
    const centerX = tempXScale(binCenter.lng);
    const centerY = tempYScale(binCenter.lat);

    // Calculate transform to center the view with initial zoom
    const translateX = (width / 2 - centerX);
    const translateY = (height / 2 - centerY);

    const final = d3.zoomIdentity.translate(translateX, translateY);
    return final;
  }, [bins, bounds, width, height, binCenter]);

  const [transform, setTransform] = useState(initialTransform);

  const zoomBehavior = useMemo(() => {
    return d3.zoom()
      .scaleExtent([MIN_ZOOM, MAX_ZOOM])
      // .touchable(() => false)
      .duration(500)
      .on('zoom', (event) => {
        const newTransform = event.transform;
        setTransform(newTransform);
        onViewChange?.();

        // Not using start, to avoid saying we are interacting on the initial mousedown event.
        setIsInteracting(true);
      })
      .on('end', () => {
        // Small delay to prevent immediate hover events after interaction ends
        setTimeout(() => setIsInteracting(false), 100);
      });
  }, [onViewChange, setIsInteracting]);

  return { zoomBehavior, transform, initialTransform, zoomPercentage: calculateZoomPercentage(transform.k) };
}

/**
 * Renders bins using D3 data binding
 * @param {Object} params - Rendering parameters
 */
function renderBins({ bins, xScale, yScale, styles, transform, binStyles, onBinClick, onMouseEnter, onMouseOut, container, metersToPixelsScale, zoomPercentage, showFillLevel }) {
  // Bind data to container
  const binGroups = container.selectAll('.bin-group')
    .data(bins, d => d.id);

  // Remove old bins
  binGroups.exit().remove();

  // Enter new bins
  const binGroupsEnter = binGroups.enter()
    .append('g')
    .attr('class', 'bin-group');

  // Merge enter and update selections
  const binGroupsMerged = binGroupsEnter.merge(binGroups);

  // Update all bins
  binGroupsMerged.each(function(bin) {
    const x = transform.applyX(xScale(bin.longitude));
    const y = transform.applyY(yScale(bin.latitude));
    const scale = transform.k;

    const style = binStyles[bin.id] || {};
    const displayName = zoomPercentage > 200 ? bin.name : /(?<num>[0-9]+)/.exec(bin.name)?.groups.num;
    const widthInPixels = metersToPixelsScale(bin.dimensions.width || (bin.dimensions.radius && bin.dimensions.radius * 2) || 10) * scale;
    const fontSizeRatio = Math.max(Math.min(widthInPixels / 60, 1), 0.25);
    const group = d3.select(this);

    // Clear existing content
    group.selectAll('*').remove();

    const pointerEnterHandler = (event) => {
      if (event.pointerType === 'mouse') {
        // We only want to show hover events for mouse interfaces
        onMouseEnter(bin, event);
      }
    };

    const pointerOutHandler = (event) => {
      if (event.pointerType === 'mouse') {
        // We only want to show hover events for mouse interfaces
        onMouseOut(event);
      }
    };

    if (bin.shape === BinTypes.RECTANGLE) {
      const heightInPixels = metersToPixelsScale(bin.dimensions.height || 10) * scale;

      const g = group.append('g')
        .style('filter', style.doubleStrokeEnabled ? 'url(#drop-shadow)' : 'none')
        .attr('transform', `translate(${x - widthInPixels / 2}, ${y - heightInPixels / 2})`);

      g.append('rect')
        .attr('width', widthInPixels)
        .attr('height', heightInPixels)
        .attr('class', `${styles.binStylesDefault} ${styles.bin} ${style.selected ? styles.selectedBin : ''}`)
        .style('fill', style.fill)
        .style('filter', style.filter)
        .style('stroke', style.stroke)
        .style('stroke-width', style.strokeWidth)
        .style('opacity', style.opacity)
        .attr('data-bin-id', bin.id)
        .on('pointerenter', pointerEnterHandler)
        .on('pointerout', pointerOutHandler)
        .on('click', () => {
          onBinClick(bin);
        });

      // Add fill level indicator for rectangular bins
      if (showFillLevel && bin.latestVolume !== undefined && bin.fullTotal && bin.fullTotal > 0) {
        const fillPercentage = (bin.latestVolume / bin.fullTotal) * 100;
        const fillHeight = (fillPercentage / 100) * heightInPixels;
        const strokeWidth = style.strokeWidth || 1;
        const inset = strokeWidth / 2;

        g.append('rect')
          .attr('width', widthInPixels - strokeWidth)
          .attr('height', fillHeight - strokeWidth)
          .attr('x', inset)
          .attr('y', heightInPixels - fillHeight + inset)
          .style('opacity', style.opacity)
          .style('fill', style.fillLevelColor || '#2196f3')
          .style('pointer-events', 'none');
      }

      if (style.doubleStrokeEnabled) {
        const extraSize = 6;
        g.append('rect')
          .style('fill', 'none')
          .attr('width', widthInPixels + extraSize)
          .attr('height', heightInPixels + extraSize)
          .attr('x', -extraSize / 2)
          .attr('y', -extraSize / 2)
          .attr('class', `${styles.bin} ${style.selected ? styles.selectedBin : ''}`)
          .style('stroke', style.doubleStrokeColour)
          .style('stroke-width', style.strokeWidth)
          .style('pointer-events', 'none');
      }

      g.append('text')
        .text(displayName)
        .style('fill', style.textColor || 'black')
        .style('pointer-events', 'none')
        .style('user-select', 'none')
        .style('text-anchor', 'middle')
        .style('font-size', `calc(1rem * ${fontSizeRatio})`)
        .attr('dy', '0.35em')
        .attr('x', widthInPixels / 2)
        .attr('y', heightInPixels / 2);
    } else {
      const radiusInPixels = metersToPixelsScale(bin.dimensions.radius || 10) * scale;
      const g = group.append('g')
        .style('filter', style.doubleStrokeEnabled ? 'url(#drop-shadow)' : 'none')
        .attr('transform', `translate(${x}, ${y})`);

      g.append('circle')
        .attr('r', radiusInPixels)
        .attr('class', `${styles.binStylesDefault} ${styles.bin} ${style.selected ? styles.selectedBin : ''}`)
        .style('fill', style.fill)
        .style('filter', style.filter)
        .style('stroke', style.stroke)
        .style('stroke-width', style.strokeWidth)
        .style('opacity', style.opacity)
        .attr('data-bin-id', bin.id)
        .on('click', () => {
          onBinClick(bin);
        })
        .on('pointerenter', pointerEnterHandler)
        .on('pointerout', pointerOutHandler);

      // Add fill level indicator for circular bins using clip path
      if (showFillLevel && bin.latestVolume !== undefined && bin.fullTotal && bin.fullTotal > 0) {
        const fillPercentage = (bin.latestVolume / bin.fullTotal) * 100;

        // Create clip path for circular fill
        const clipPathId = `clip-${bin.id.replace(/[^a-zA-Z0-9]/g, '')}`;

        // Create the clip path
        const defs = g.append('defs');
        const clipPath = defs.append('clipPath')
          .attr('id', clipPathId);

        // Calculate the fill area - from bottom up, accounting for stroke
        const strokeWidth = style.strokeWidth || 1;
        const insetRadius = radiusInPixels - strokeWidth / 2;
        const fillHeight = (fillPercentage / 100) * (insetRadius * 2);
        const fillY = insetRadius - fillHeight;

        clipPath.append('rect')
          .attr('x', -insetRadius)
          .attr('y', fillY)
          .attr('width', insetRadius * 2)
          .attr('height', fillHeight);

        // Add the filled circle with clip path
        g.append('circle')
          .attr('r', insetRadius)
          .style('fill', style.fillLevelColor || '#2196f3')
          .style('opacity', style.opacity)
          .style('pointer-events', 'none')
          .style('clip-path', `url(#${clipPathId})`);

        if (style.doubleStrokeEnabled) {
          const extraSize = 3;
          g.append('circle')
            .style('fill', 'none')
            .attr('r', radiusInPixels + extraSize)
            .attr('x', extraSize / -2)
            .attr('y', extraSize / -2)
            .attr('class', `${styles.bin} ${style.selected ? styles.selectedBin : ''}`)
            .style('stroke', style.doubleStrokeColour)
            .style('stroke-width', style.strokeWidth)
            .style('pointer-events', 'none');
        }
      }

      g.append('text')
        .text(displayName)
        .style('pointer-events', 'none')
        .style('user-select', 'none')
        .attr('dy', '0.35em')
        .style('font-size', `calc(1rem * ${fontSizeRatio})`)
        .style('fill', style.textColor || 'black')
        .style('text-anchor', 'middle');
    }

    // Ensure selected/double-stroke bins render above neighbors so the outline isn't occluded
    if (style.doubleStrokeEnabled || style.selected) {
      group.raise();
    }
  });
}

/**
 * Renders background shapes using D3 data binding
 * @param {Object} params - Rendering parameters
 */
function renderBackgroundShapes({ backgroundShapes, xScale, yScale, transform, container, metersToPixelsScale }) {
  // Bind data to container
  const shapeGroups = container.selectAll('.background-shape-group')
    .data(backgroundShapes, d => d.id);

  // Remove old shapes
  shapeGroups.exit().remove();

  // Enter new shapes
  const shapeGroupsEnter = shapeGroups.enter()
    .append('g')
    .attr('class', 'background-shape-group');

  // Merge enter and update selections
  const shapeGroupsMerged = shapeGroupsEnter.merge(shapeGroups);

  // Update all shapes
  shapeGroupsMerged.each(function(d) {
    const shape = d;
    const x = transform.applyX(xScale(shape.longitude));
    const y = transform.applyY(yScale(shape.latitude));
    const scale = transform.k;

    const group = d3.select(this);

    // Clear existing content
    group.selectAll('*').remove();

    if (shape.type === BackgroundShapeTypes.RECTANGLE) {
      // Convert dimensions to pixels using the scale
      const widthInPixels = metersToPixelsScale(shape.dimensions.width || 10) * scale;
      const heightInPixels = metersToPixelsScale(shape.dimensions.height || 10) * scale;

      group.append('rect')
        .attr('x', x - widthInPixels / 2)
        .attr('y', y - heightInPixels / 2)
        .attr('width', widthInPixels)
        .attr('height', heightInPixels)
        .attr('class', `background-shape ${shape.type}`)
        .style('fill', shape.style.fill)
        .style('stroke', shape.style.stroke)
        .style('stroke-width', shape.style.strokeWidth)
        .style('opacity', shape.style.opacity);
    } else if (shape.type === BackgroundShapeTypes.CIRCLE) {
      // Convert radius to pixels using the scale
      const radiusInPixels = metersToPixelsScale(shape.dimensions.radius || 10) * scale;

      group.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', radiusInPixels)
        .attr('class', `background-shape ${shape.type}`)
        .style('fill', shape.style.fill)
        .style('stroke', shape.style.stroke)
        .style('stroke-width', shape.style.strokeWidth)
        .style('opacity', shape.style.opacity);
    } else if (shape.type === BackgroundShapeTypes.POLYGON) {
      const points = shape.dimensions.points.map(p => ({
        x: transform.applyX(xScale(p.longitude)),
        y: transform.applyY(yScale(p.latitude)),
      }));

      group.append('polygon')
        .attr('points', points.map(p => `${p.x},${p.y}`).join(' '))
        .attr('class', `background-shape ${shape.type}`)
        .style('fill', shape.style.fill)
        .style('stroke', shape.style.stroke)
        .style('stroke-width', shape.style.strokeWidth)
        .style('opacity', shape.style.opacity);
    } else if (shape.type === BackgroundShapeTypes.LINE) {
      const points = shape.dimensions.points.map(p => ({
        x: transform.applyX(xScale(p.longitude)),
        y: transform.applyY(yScale(p.latitude)),
      }));

      group.append('line')
        .attr('x1', points[0].x)
        .attr('y1', points[0].y)
        .attr('x2', points[1].x)
        .attr('y2', points[1].y)
        .attr('class', `background-shape ${shape.type}`)
        .style('stroke', shape.style.stroke)
        .style('stroke-width', shape.style.strokeWidth)
        .style('opacity', shape.style.opacity);
    } else if (shape.type === BackgroundShapeTypes.TEXT) {
      const text = shape.dimensions.text;
      const fontSize = metersToPixelsScale(shape.dimensions.fontSize || 10) * scale;
      const angle = shape.dimensions.angle || 0;

      group.append('text')
        .text(text)
        .style('user-select', 'none')
        .attr('class', `background-shape ${shape.type}`)
        .style('fill', shape.style.fill)
        .style('opacity', shape.style.opacity ?? 1)
        .style('font-size', `${fontSize}px`)
        .attr('transform', `translate(${x},${y}) rotate(${angle})`);
    } else if (shape.type === BackgroundShapeTypes.IMAGE) {
      const widthInPixels = metersToPixelsScale(shape.dimensions?.width || 20) * scale;
      const heightInPixels = metersToPixelsScale(shape.dimensions?.height || 20) * scale;
      const angle = shape.dimensions?.angle || 0;

      group.append('image')
        .attr('href', shape.href)
        .attr('width', widthInPixels)
        .attr('height', heightInPixels)
        .attr('class', `background-shape ${shape.type}`)
        .style('user-select', 'none')
        .style('opacity', shape.style?.opacity || 1)
        .attr('transform', `translate(${x - widthInPixels / 2},${y - heightInPixels / 2}) rotate(${angle})`);
    }
  });
}

/**
 * Main bin map visualization component
 * @param {Object} props - Component props
 * @param {Bin[]} props.bins - Array of bin data
 * @param {Object} props.binStyles - Styles for specific bins by ID
 * @param {BackgroundShape[]} props.backgroundShapes - Array of background shapes to render
 * @param {Function} props.onBinClick - Callback when bin is clicked
 * @param {number} props.width - Container width
 * @param {number} props.height - Container height
 * @returns {JSX.Element} Bin map component
 */
export default function InventoryMap({
  bins = [],
  binStyles = {},
  backgroundShapes = [],
  onBinClick,
  width = 800,
  height = 600,
  showFillLevel = false,
  handleMouseEnter,
  handleMouseLeave,
  setIsInteracting,
  clearHover,
}) {
  const styles = useStyles();
  const bounds = useMapBounds(bins, backgroundShapes);
  const { xScale, yScale } = useMapScales(bounds, width, height);
  const metersToPixelsScale = useMetersToPixelsScale(bounds, width, height);

  const isFirstRender = useRef(true);

  const { ref: containerRef } = useResizeObserver({
    // We can't be sure we are still hovering a bin if the view has changed
    // & we do not get mouse enter/leave callbacks
    onResize: () => clearHover(),
  });

  const { zoomBehavior, transform, initialTransform, zoomPercentage } = useZoomBehavior(width, height, clearHover, bins, bounds, setIsInteracting);

  // Handle map clicks to log lat/long coordinates
  const handleMapClick = useCallback((event) => {
    // Get the click coordinates relative to the SVG
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Apply inverse transform to get the original pixel coordinates
    // D3 transform has applyX/applyY, so we need to use the inverse methods
    const originalX = transform.invertX(clickX);
    const originalY = transform.invertY(clickY);

    // Convert pixel coordinates back to lat/long using inverse scales
    const longitude = xScale.invert(originalX);
    const latitude = yScale.invert(originalY);

    console.info(`Map click coordinates: ${latitude}, ${longitude}`);
  }, [transform, xScale, yScale]);

  const resetZoom = useRef(() => {});
  const svgRef = useD3(
    (svg) => {
      svg.selectAll('*').remove();

      const defs = svg.append('defs');

      // drop shadow
      const filter = defs.append('filter')
        .attr('id', 'drop-shadow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');

      filter.append('feDropShadow')
        .attr('dx', '2')
        .attr('dy', '2')
        .attr('stdDeviation', '2')
        .attr('flood-color', 'rgba(0,0,0,0.5)');

      svg.call(zoomBehavior).on('dblclick.zoom', null);

      if (isFirstRender.current) {
        svg.call(zoomBehavior.transform, initialTransform);
        svg.call(zoomBehavior.scaleBy, INITIAL_ZOOM);
        resetZoom.current = () => {
          svg.call(zoomBehavior.transform, initialTransform);
          svg.call(zoomBehavior.scaleTo, INITIAL_ZOOM);
        };
        isFirstRender.current = false;
      }

      // Create main group for all content
      const g = svg.append('g');

      // Render background shapes first (so they appear behind bins)
      renderBackgroundShapes({
        backgroundShapes,
        xScale,
        yScale,
        transform,
        styles,
        container: g,
        metersToPixelsScale,
      });

      // Render bins using D3 data binding
      renderBins({
        bins,
        xScale,
        yScale,
        transform,
        binStyles,
        styles,
        onBinClick,
        onMouseEnter: handleMouseEnter,
        onMouseOut: handleMouseLeave,
        container: g,
        metersToPixelsScale,
        zoomPercentage,
        showFillLevel,
      });
    },
    [bins, backgroundShapes, xScale, yScale, transform, binStyles, onBinClick, handleMouseEnter, handleMouseLeave, zoomBehavior, metersToPixelsScale, initialTransform, showFillLevel],
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <svg
        ref={svgRef}
        className={styles.svg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onClick={handleMapClick}
      />

      <InventoryMapZoomControl zoomPercentage={zoomPercentage} onClick={resetZoom.current} />
    </div>
  );
}
